import { Button, Heading, Text } from '@teliaee/design-system';
import { observer } from 'mobx-react';
import * as React from 'react';
import UiNotice from '../components/UiNotice';
import LogoutButton from '../mandate/LogoutButton';
import MandateListItems from '../mandate/MandateListItems';
import MandateSearch from '../mandate/MandateSearch';
import GaDataStore from '../stores/GaDataStore';
import PaddedSpinner from '../util/PaddedSpinner';
import { runInTeliaRumService } from '../util/TeliaOtelUtil';
import WrapWithContainerInFullPage from '../wrapper/WrapWithContainerInFullPage';
import MandatesErrorPage from './MandatesErrorPage';
import useStores from '../stores/UseStores';
import Config from '../Config';
import { MandateCustomer } from '../CommonInterfaces';
import PasskeyOfferRegister from '../components/PasskeyOfferRegister';

const MandatesPage = observer((): React.JSX.Element => {
  const {
    mandateStore: { isFetching, isContinueFetching, isError, hideLogout, b2bCustomers, b2cCustomers, loadMandates },
    translateStore: { translate },
    homeStore: { initialComponent },
    uiStore: { hasNotice },
    passkeyStore,
  } = useStores();
  const [searchFilter, setSearchFilter] = React.useState<string>('');
  const [gaSearchEventSent, setGaSearchEventSent] = React.useState<boolean>(false);

  React.useEffect(() => {
    runInTeliaRumService('Esindatava valik', async () => {
      await loadMandates();
    });
  }, []);

  const onSearchChange = React.useCallback((value: string) => {
    if (!searchFilter && !gaSearchEventSent) {
      GaDataStore.searchMandate();
      setGaSearchEventSent(true);
    }
    setSearchFilter(value.toLowerCase());
  }, []);

  const applySearchFilter = (name: string) => {
    return name.toLowerCase().indexOf(searchFilter) > -1;
  };

  const applyBrandFilter = (mandate: MandateCustomer) => {
    if (!Config.brand || mandate.brands!.length === 0) {
      return true;
    }
    return mandate.brands!.includes(Config.brand);
  };

  const getB2cMandates = () => {
    return b2cCustomers.filter((c) => applyBrandFilter(c) && (!searchFilter || applySearchFilter(c.displayName)));
  };

  const getB2bMandates = () => {
    return b2bCustomers.filter((c) => applyBrandFilter(c) && (!searchFilter || applySearchFilter(c.displayName)));
  };

  const returnToSelfserviceButton = () => {
    if ((document.referrer.includes('telia.ee') || Config.brand === 'telia') && initialComponent === 'MANDATES') {
      return (
        <Button
          text={translate('mandates.returnToSelfservice')}
          onClick={() => (window!.parent.location = Config.selfServiceUrl)}
          iconRight="#arrow-right"
          iconRightSize="xs"
          className="visible"
        />
      );
    }
  };

  if (passkeyStore.offerRegisterModalVisible) {
    return <PasskeyOfferRegister />;
  }

  if (isFetching) {
    return <PaddedSpinner />;
  }

  if (isError || isContinueFetching) {
    return <MandatesErrorPage />;
  }

  if (hasNotice) {
    return (
      <WrapWithContainerInFullPage>
        <UiNotice />
      </WrapWithContainerInFullPage>
    );
  }

  return (
    <WrapWithContainerInFullPage>
      <Text>
        {!hideLogout && <LogoutButton />}
        {returnToSelfserviceButton()}
        <Heading id="mandates-title" className="h4">
          {translate('mandates.title')}
        </Heading>
        {b2bCustomers.length >= 10 && <MandateSearch onSearchChange={onSearchChange} />}
        <MandateListItems b2cMandates={getB2cMandates()} b2bMandates={getB2bMandates()} />
      </Text>
    </WrapWithContainerInFullPage>
  );
});

export default MandatesPage;
